/**
 * File-download side effect for WagePro, delegating to `@ledger/platform`.
 *
 * This was a local anchor + object-URL click. That is a silent NO-OP inside
 * the Android WebView that the Capacitor shell (`apps/wagepro-mobile`) runs:
 * Android never starts a download for a `blob:` URL on an `<a download>`, so
 * on a real phone EVERY export in this app did nothing at all — the payroll
 * import template, the statutory reports, payslip PDFs, the bank
 * disbursement file, and the employee directory export. Nothing threw and
 * nothing was logged, which is why it reads as "the button is dead".
 *
 * `@ledger/platform` already owns this exact branch and is the same
 * abstraction `apps/ledger-web` uses (`platform.download`):
 *
 *   - browser  → transient anchor click, with `rel="noopener"` and a deferred
 *                `revokeObjectURL` so Safari has time to start the transfer
 *                (the local copy revoked immediately, which could truncate a
 *                Safari download).
 *   - native   → `Filesystem.writeFile` into `Directory.Documents`, a real
 *                file on the device rather than a no-op.
 *
 * Kept as a named wrapper rather than importing `platform` at each of the
 * five call sites: one seam, one place for this rationale, and no churn in
 * the screens if the platform contract ever changes.
 *
 * NOTE the signature is now async — the native leg is genuinely
 * asynchronous. Callers must `await` (or `void`) it; a floating promise here
 * would report success before the file is written.
 */
import { platform } from '@ledger/platform';

export async function triggerDownload(
  blob: Blob,
  filename: string,
): Promise<void> {
  await platform.download(blob, filename);
}
